import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/errors';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useDocuments } from '../../documents/api/useDocuments';
import { useMachines } from '../../machines/api/useMachines';
import { askQuestion, setConversationDocumentScope } from '../api/conversations.api';
import { useConversation } from '../api/useConversations';
import { ConversationComposer } from '../components/ConversationComposer';
import { useChatAutoScroll } from '../hooks/useChatAutoScroll';
import { useThreadHistoryLoader } from '../hooks/useThreadHistoryLoader';
import { ConversationTurn } from '../components/ConversationTurn';
import { EvidencePanel } from '../components/EvidencePanel';
import { ThinkingState } from '../components/ThinkingState';
import { promoteConversation } from '../../investigations/api/investigations.api';
import { StartInvestigationDialog } from '../../investigations/components/StartInvestigationDialog';
import type { GroundedAnswerDto } from '../types/conversation';

export function ConversationPage() {
  const { machineId = '', conversationId = '' } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const conversation = useConversation(conversationId, auth.can(permissions.conversationRead));
  const machines = useMachines();
  const documents = useDocuments(machineId, auth.can(permissions.documentsRead));
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<GroundedAnswerDto | null>(null);
  const [scopeOverride, setScopeOverride] = useState<string[] | null>(null);
  const [investigateTurn, setInvestigateTurn] = useState<{ turnId: string; question: string } | null>(null);
  const [investigationBusy, setInvestigationBusy] = useState(false);
  const [investigationError, setInvestigationError] = useState<string | null>(null);
  const scopeIds = scopeOverride ?? conversation.data?.conversation_memory?.current_document_ids ?? [];
  const scopedDocuments = (documents.data?.documents ?? []).filter((document) => scopeIds.includes(document.id));
  const allTurns = conversation.data?.turns ?? [];
  const chatScroll = useChatAutoScroll({
    threadKey: conversationId,
    itemCount: allTurns.length,
    busy,
  });
  const historyLoader = useThreadHistoryLoader({
    viewportRef: chatScroll.viewportRef,
    itemCount: allTurns.length,
    fetchOlder: conversation.fetchOlder,
  });
  const hiddenTurnCount = Math.max(0, (conversation.data?.turn_count ?? allTurns.length) - allTurns.length);

  if (!auth.can(permissions.conversationRead)) return <Navigate to={`/machines/${machineId}`} replace />;
  if (conversation.data && conversation.data.conversation.machine_id !== machineId) return <Navigate to={`/machines/${machineId}/conversations`} replace />;

  async function submit(question: string) {
    chatScroll.scrollToLatest();
    setBusy(true);
    setError(null);
    try {
      await askQuestion(conversationId, question);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(auth.activeCustomerId, conversationId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations(auth.activeCustomerId, machineId) }),
      ]);
    } catch (value) {
      setError(questionError(value));
      throw value;
    } finally {
      setBusy(false);
    }
  }

  async function startInvestigation(title: string, objective: string) {
    if (!investigateTurn) return;
    setInvestigationBusy(true);
    setInvestigationError(null);
    try {
      const result = await promoteConversation(conversationId, title, objective, [investigateTurn.turnId]);
      await queryClient.invalidateQueries({ queryKey: queryKeys.investigations(auth.activeCustomerId, machineId) });
      navigate(`/machines/${machineId}/investigations/${result.investigation.investigation_id}`);
    } catch (value) {
      setInvestigationError(questionError(value));
    } finally {
      setInvestigationBusy(false);
    }
  }

  async function clearScope() {
    try {
      await setConversationDocumentScope(conversationId, []);
      setScopeOverride([]);
      await queryClient.invalidateQueries({ queryKey: queryKeys.conversation(auth.activeCustomerId, conversationId) });
    } catch (value) {
      setError(questionError(value));
    }
  }

  if (conversation.isLoading) return <section className="r42-conversation-page"><div className="r42-inline-state">Loading conversation…</div></section>;
  if (conversation.isError || !conversation.data) return <section className="r42-conversation-page"><div className="r42-inline-state r42-inline-state--error"><span>Route42 couldn't load this conversation.</span><button className="btn btn-sm btn-outline-secondary" onClick={() => void conversation.refetch()}>Try again</button></div></section>;

  return (
    <section className="r42-conversation-page">
      <header className="r42-work-head">
        <div><h1>{conversation.data.conversation.title || machine?.name || 'Machine conversation'}</h1><p>{machine?.name ? `Machine: ${machine.name}` : 'Route42 grounded machine conversation'}</p></div>
        <span className="r42-intel-status">Grounding <b>{scopeIds.length ? 'Document scoped' : 'Machine wide'}</b></span>
      </header>

      {scopeIds.length ? (
        <div className="r42-scope-bar"><i className="bi bi-file-earmark-text" aria-hidden="true" /><span>Context: <strong>{scopedDocuments.map((document) => document.filename).join(', ') || `${scopeIds.length} document${scopeIds.length === 1 ? '' : 's'}`}</strong></span><button onClick={() => void clearScope()}>Clear</button></div>
      ) : null}

      <div className="r42-conversation-scroll" ref={chatScroll.viewportRef} onScroll={chatScroll.onScroll}>
        <div className="r42-conversation-turns" data-testid="conversation-thread">
          {conversation.hasOlder ? <div className="r42-thread-history-control"><button type="button" onClick={() => void historyLoader.loadOlder()} disabled={conversation.isFetchingOlder}><i className="bi bi-clock-history" aria-hidden="true" /> {conversation.isFetchingOlder ? 'Loading earlier messages…' : 'Load earlier messages'}{hiddenTurnCount ? <span>{hiddenTurnCount}</span> : null}</button></div> : null}
          {allTurns.map((turn) => <ConversationTurn key={turn.turn_id} turn={turn} onEvidence={() => turn.answer && setEvidence(turn.answer)} onInvestigate={auth.can(permissions.investigationCreate) && turn.answer ? () => { setInvestigationError(null); setInvestigateTurn({ turnId: turn.turn_id, question: turn.question }); } : undefined} />)}
          {busy ? <ThinkingState /> : null}
          {error ? <div className="r42-question-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>{error}</span></div> : null}
        </div>
      </div>

      <div className="r42-composer-wrap">
        <ConversationComposer machineName={machine?.name ?? 'this machine'} disabled={!auth.can(permissions.questionAsk)} busy={busy} onSubmit={submit} />
        <div id="r42-mobile-ace-launcher-slot" className="r42-mobile-ace-launcher-slot" />
        <p>{scopeIds.length ? 'This conversation is currently grounded to the selected document context.' : 'New answers use the latest grounded machine evidence available to Route42.'}</p>
      </div>

      {evidence ? <EvidencePanel machineId={machineId} answer={evidence} documents={documents.data?.documents ?? []} onClose={() => setEvidence(null)} /> : null}
      {investigateTurn ? <StartInvestigationDialog mode="promote" title={investigationTitle(investigateTurn.question)} objective={investigationObjective(investigateTurn.question)} busy={investigationBusy} error={investigationError} onClose={() => { if (!investigationBusy) { setInvestigateTurn(null); setInvestigationError(null); } }} onSubmit={startInvestigation} /> : null}
    </section>
  );
}

function questionError(value: unknown): string {
  if (value instanceof ApiError) {
    if (value.code === 'MACHINE_KNOWLEDGE_NOT_READY' || value.code === 'MACHINE_MEMORY_NOT_READY') return 'Route42 is still learning this machine. Add or finish processing grounded machine information before asking this question.';
    if (value.code === 'GROUNDED_ANSWER_INVALID') return 'Route42 could not verify the grounding for that answer, so it was not shown. Try asking the question another way.';
    return value.message;
  }
  return 'Route42 could not answer that question. Please try again.';
}

// Keep promoted Investigation titles intact up to the contract limit; the detail page wraps long titles.
function investigationTitle(question: string): string {
  const clean = question.replace(/\s+/g, ' ').trim().replace(/[?!.]+$/, '');
  if (clean.length <= 200) return clean;
  const candidate = clean.slice(0, 197);
  const boundary = candidate.lastIndexOf(' ');
  return `${candidate.slice(0, boundary > 140 ? boundary : 197).trimEnd()}…`;
}
function investigationObjective(question: string): string { const clean = question.replace(/\s+/g, ' ').trim(); return `Determine what the grounded machine evidence supports about: ${clean}`.slice(0, 1200); }
